// Motore di calcolo puro del costruttore di costi deducibili (P.IVA regime
// ordinario/semplificato). Nessuna dipendenza dal DOM: viene riusato dal
// controller di pagina (js/costiDeducibiliUI.js via js/pivaOrdinaria.js) e dai
// test (tests/costiDeducibili.test.js).
//
// Il motore non conosce le regole fiscali: le legge dal catalogo
// (data/costi-deducibili.json), passato come parametro. Per ogni voce applica,
// in quest'ordine: tetto sulla base -> percentuale del tipo contribuente ->
// plafond dinamico sui ricavi (condiviso tra le categorie che puntano allo
// stesso plafond, consumato nell'ordine del catalogo). Lavora al centesimo:
// l'arrotondamento a euro intero avviene solo quando la UI scrive il campo
// #costi. Riferimenti normativi e verifica delle regole in
// docs/costi-deducibili-piva-teoria-fiscale.md.

const round2 = (value) => Math.round((value + Number.EPSILON) * 100) / 100;

const importoValido = (value) => {
    const n = parseFloat(value);
    return Number.isFinite(n) && n > 0 ? n : 0;
};

// Deriva il tipo contribuente (professionista | impresa | agente) da cassa e
// forma attivita'. La cassa vince sulla forma: con forma "altro" il form
// sblocca tutte le casse, quindi e' l'unico segnale affidabile. Coerente con
// cassaByForma in js/pivaOrdinaria.js.
export function tipoContribuente(formaAttivita, cassaId, catalogo) {
    const mappa = catalogo?.tipi_contribuente || {};
    for (const [tipo, casse] of Object.entries(mappa)) {
        if (Array.isArray(casse) && casse.includes(cassaId)) return tipo;
    }
    if (formaAttivita === 'ditta_artigiana' || formaAttivita === 'ditta_commerciale') return 'impresa';
    if (formaAttivita === 'libero_senza_cassa' || formaAttivita === 'libero_con_cassa') return 'professionista';
    return null;
}

// Limite in euro di un plafond dato il suo imponibile di riferimento (oggi
// sempre i ricavi): percentuale secca oppure scaglioni [soglia, pct] con
// soglia null per l'ultimo scaglione aperto (art. 108 c. 2 TUIR).
export function limitePlafond(plafondDef, ricavi) {
    const base = Math.max(0, parseFloat(ricavi) || 0);
    if (!plafondDef) return 0;
    if (Array.isArray(plafondDef.scaglioni_pct)) {
        let limite = 0;
        let precedente = 0;
        for (const [soglia, pct] of plafondDef.scaglioni_pct) {
            const tetto = soglia === null ? base : Math.min(base, soglia);
            if (tetto > precedente) limite += (tetto - precedente) * (pct / 100);
            precedente = Math.max(precedente, tetto);
            if (soglia !== null && base <= soglia) break;
        }
        return round2(limite);
    }
    return round2(base * ((plafondDef.aliquota_pct || 0) / 100));
}

const quotaIvaDetraibile = (iva, tipo) => {
    if (!iva) return 0;
    const quota = iva.quota_detraibile_pct;
    if (quota && typeof quota === 'object') return quota[tipo] || 0;
    return quota || 0;
};

// input: {
//   voci,    // [{ categoriaId, importo }] importi annui al netto IVA; voci con
//            // id sconosciuto o non applicabile al tipo vengono ignorate
//   ricavi,  // ricavi/compensi annui, base dei plafond dinamici
//   tipo     // 'professionista' | 'impresa' | 'agente' (default professionista,
//            // il set di regole piu' restrittivo)
// }
// catalogo: contenuto di data/costi-deducibili.json.
export function calcolaCostiDeducibili(input, catalogo) {
    const tipo = ['professionista', 'impresa', 'agente'].includes(input?.tipo)
        ? input.tipo
        : 'professionista';
    const ricavi = Math.max(0, parseFloat(input?.ricavi) || 0);

    // Somma per categoria: piu' voci sulla stessa categoria si accumulano.
    const importi = new Map();
    for (const voce of Array.isArray(input?.voci) ? input.voci : []) {
        const importo = importoValido(voce?.importo);
        if (!voce?.categoriaId || importo <= 0) continue;
        importi.set(voce.categoriaId, (importi.get(voce.categoriaId) || 0) + importo);
    }

    // Stato dei plafond: limite calcolato sui ricavi, consumo progressivo
    // nell'ordine del catalogo (rappresentanza prima degli omaggi, per il
    // professionista che condivide l'1%).
    const plafonds = {};
    const statoPlafond = (id) => {
        if (!plafonds[id]) {
            plafonds[id] = { limite: limitePlafond(catalogo?.plafond?.[id], ricavi), usato: 0, saturato: false };
        }
        return plafonds[id];
    };

    const righe = [];
    let totaleSostenuto = 0;
    let totaleDedotto = 0;
    let totaleIvaStimata = 0;

    for (const cat of Array.isArray(catalogo?.categorie) ? catalogo.categorie : []) {
        const importo = round2(importi.get(cat.id) || 0);
        if (importo <= 0) continue;
        if (Array.isArray(cat.solo) && !cat.solo.includes(tipo)) continue;
        const regola = cat.regole?.[tipo];
        if (!regola) continue;

        const pct = Math.max(0, regola.pct || 0);
        const tettoBase = Number.isFinite(regola.tetto_base) ? regola.tetto_base : null;
        const base = tettoBase !== null ? Math.min(importo, tettoBase) : importo;
        const dedottoPre = round2(base * (pct / 100));

        let dedotto = dedottoPre;
        let plafondLimite = null;
        let plafondResiduo = null;
        if (regola.plafond) {
            const stato = statoPlafond(regola.plafond);
            const residuo = round2(Math.max(0, stato.limite - stato.usato));
            dedotto = Math.min(dedottoPre, residuo);
            stato.usato = round2(stato.usato + dedotto);
            if (stato.usato >= stato.limite) stato.saturato = true;
            plafondLimite = stato.limite;
            plafondResiduo = round2(Math.max(0, stato.limite - stato.usato));
        }

        let limiteApplicato = null;
        if (pct === 0) {
            limiteApplicato = 'indeducibile';
        } else if (dedotto < dedottoPre) {
            limiteApplicato = 'plafond';
        } else if (tettoBase !== null && importo > tettoBase) {
            limiteApplicato = 'tetto';
        } else if (pct < 100) {
            limiteApplicato = 'percentuale';
        }

        const ivaDetraibileStimata = round2(
            importo * ((cat.iva?.aliquota_pct || 0) / 100) * (quotaIvaDetraibile(cat.iva, tipo) / 100)
        );

        righe.push({
            categoriaId: cat.id,
            sostenuto: importo,
            dedotto: round2(dedotto),
            ivaDetraibileStimata,
            limiteApplicato,
            dettaglio: {
                pct,
                tettoBase,
                plafondId: regola.plafond || null,
                plafondLimite,
                plafondResiduo
            }
        });
        totaleSostenuto = round2(totaleSostenuto + importo);
        totaleDedotto = round2(totaleDedotto + dedotto);
        totaleIvaStimata = round2(totaleIvaStimata + ivaDetraibileStimata);
    }

    return {
        righe,
        totaleSostenuto,
        totaleDedotto,
        totaleNonDedotto: round2(totaleSostenuto - totaleDedotto),
        totaleIvaStimata,
        plafonds
    };
}
